import React, { useEffect, useMemo, useState } from 'react';

export interface ProcessWaitingOverlayProps {
  open: boolean;
  title?: string;
  subtitle?: string;
  pollCount?: number;
  latestStatus?: string;
}

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export default function ProcessWaitingOverlay({
  open,
  title = 'Generating one-pager…',
  subtitle = 'This may take a minute or two',
  pollCount,
  latestStatus,
}: ProcessWaitingOverlayProps) {
  const [elapsedTime, setElapsedTime] = useState<number>(0);

  useEffect(() => {
    if (!open) {
      setElapsedTime(0);
      return;
    }

    const interval = window.setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);

    return () => window.clearInterval(interval);
  }, [open]);

  const timeText = useMemo(() => formatTime(elapsedTime), [elapsedTime]);

  if (!open) return null;

  return (
    <div className="fixed inset-y-0 left-[180px] z-[60] w-[calc(100%-180px)]">
      <div className="absolute inset-0 bg-[#282c34]/90 backdrop-blur-sm" />
      <div className="relative flex h-full w-full items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="relative mx-auto mb-8 h-40 w-40">
            <div className="absolute inset-0 rounded-full border-4 border-[#8aa1c1]/25" />
            <div className="absolute inset-0 rounded-full border-4 border-t-[#8aa1c1] border-r-[#4f83ff] border-b-transparent border-l-transparent animate-spin" />
            <div className="absolute inset-4 rounded-full border-4 border-[#4f83ff]/20" />
            <div className="absolute inset-4 rounded-full border-4 border-t-transparent border-r-transparent border-b-[#4f83ff] border-l-[#8aa1c1] animate-spin-slow" />
            <div className="absolute inset-8 rounded-full border-4 border-[#8aa1c1]/20" />
            <div className="absolute inset-8 rounded-full border-4 border-t-[#8aa1c1] border-r-transparent border-b-transparent border-l-transparent animate-spin-reverse" />
          </div>

          <h2 className="mb-2 text-2xl font-bold text-white">{title}</h2>
          <p className="mb-6 text-[#b6c4dd]">{subtitle}</p>

          <div className="inline-block rounded-lg bg-white/10 px-6 py-4">
            <div className="mb-1 text-sm text-[#b6c4dd]">Time elapsed</div>
            <div className="font-mono text-3xl font-bold text-white">{timeText}</div>
          </div>

          <div className="mt-4 flex flex-col items-center gap-1 text-sm text-[#b6c4dd]">
            {typeof pollCount === 'number' ? <div>Polls: {pollCount}</div> : null}
            {latestStatus ? <div>Latest status: {latestStatus}</div> : null}
          </div>

          <div className="mt-8 flex justify-center gap-2">
            <div className="h-2 w-2 rounded-full bg-[#4f83ff] animate-bounce" />
            <div className="h-2 w-2 rounded-full bg-[#4f83ff] animate-bounce delay-100" />
            <div className="h-2 w-2 rounded-full bg-[#4f83ff] animate-bounce delay-200" />
          </div>
        </div>
      </div>
    </div>
  );
}
